import { Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { SkeletonText } from 'utoniq-ui'

import {
  ArtistGachaDetailFragment,
  CurrentUserFragment,
  GachaRollType,
  GachasDetailConnectionFragment,
  GachaTicketConnectionFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { GpsClientInfo } from '@/components/organisms/gps'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { getActiveMembershipSubscriptionProductPlan } from '@/core/membership'
import { useIsInside } from '@/core/native/geolocation'

import { DeactivationMemberCell } from './DeactivationMemberCell'
import { DescriptionDrawer } from './DescriptionDrawer'
import { GachaInformation } from './GachaInformation'
import { GachaMembershipTag } from './GachaMembershipTag'
import { GachaSliderCell } from './GachaSliderCell'
import { GpsInformation } from './GpsInformation'
import { RemainingItemCount } from './RemainingItemCount'
import { TriggerButtons } from './TriggerButtons'

type Props = {
  artistId: string
  /** 当該アーティストのデータ */
  artist: Maybe<ArtistGachaDetailFragment>
  /** ガチャが利用可能かどうか */
  available: boolean
  /** データローディング状態 */
  loading: boolean
  /** ガチャ一覧配列の初期表示インデックス */
  initialSlide: number | undefined
  /** 表示されているガチャのデータ */
  currentGacha: Maybe<GachasDetailConnectionFragment['edges'][number]['node']>
  /** ガチャ一覧のデータ */
  gachas: Maybe<GachasDetailConnectionFragment>
  /** ガチャを回している最中かどうか */
  gachaRolling: boolean
  /** アプリのガチャチケット */
  gachaTicket: Maybe<GachaTicketConnectionFragment>
  /** 表示しているガチャID */
  gachaId: string
  /** ユーザー位置情報 */
  gpsClientInfo: GpsClientInfo
  /** ユーザー情報 */
  user: Maybe<CurrentUserFragment>
  /** カルーセルをスライドした後の処理 */
  onAfterChange: (currentSlide: number) => void
  /** ガチャを回すボタンを押下した時の処理 */
  onClickTrigger: (count: number) => Promise<void>
}

/**
 * @description
 * ガチャの画像・情報・回すボタンの表示
 */
export const GachaBody = ({
  artistId,
  artist,
  available,
  loading,
  initialSlide,
  currentGacha,
  gachas,
  gachaRolling,
  gachaTicket,
  gachaId,
  gpsClientInfo,
  user,
  onAfterChange,
  onClickTrigger,
}: Props) => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)
  const { radius, ...gachaGpsLocation } = currentGacha?.gpsData || {}
  const { isInside } = useIsInside({
    locations: [gachaGpsLocation, gpsClientInfo.currentLocation],
    radius: currentGacha?.gpsData?.radius,
  })

  const { membershipType, productTitle } = useMemo<{
    membershipType: 'planOnly' | 'allMembership'
    productTitle?: string
  }>(() => {
    const product = getActiveMembershipSubscriptionProductPlan(
      artist,
      currentGacha?.planId
    )
    return {
      productTitle: product?.title,
      membershipType: product ? 'planOnly' : 'allMembership',
    }
  }, [artist, currentGacha?.planId])
  const isShowModalPersonalInfoRequire = !!(
    currentGacha?.isPremium &&
    artist?.patron?.isPersonalInformationRequired &&
    user?.isMembershipPersonalInformationRequired
  )

  if (loading) return <LoadingCell />
  if (!available || !gachas?.edges?.length) return <EmptyCell />

  if (initialSlide === undefined) return null

  return (
    <Box>
      {/**
       * ガチャのカルーセル部分
       */}
      <GachaSliderCell
        initialSlide={initialSlide}
        onAfterChange={onAfterChange}
        gachas={gachas}
      />

      <CellFrame sx={{ pt: 3, pb: 5 }}>
        {/**
         * ガチャの詳細データ表示
         */}
        <GachaInformation
          artistId={artistId}
          gachaId={gachaId}
          endAt={currentGacha?.formattedEndAt}
          deliveryDateText={currentGacha?.deliveryDateText}
          onClickDetail={() => setOpenDrawer(true)}
        />
        {!currentGacha?.isAccessible && (
          <DeactivationMemberCell
            artistId={artistId}
            artist={artist}
            type={membershipType}
            productTitle={productTitle}
          />
        )}
        {/**
         * GPSガチャのデータ表示
         */}
        <GpsInformation
          gpsClientInfo={gpsClientInfo}
          gpsGachaData={currentGacha?.gpsData}
          isInside={isInside}
        />
        <Stack spacing={2} sx={{ px: 5, pt: 5 }}>
          {currentGacha?.isPremium && (
            <Stack alignItems="flex-end">
              <GachaMembershipTag type={membershipType} />
            </Stack>
          )}
          {/**
           * ガチャを回すボタン
           */}
          <TriggerButtons
            loading={gachaRolling}
            isAccessible={!!currentGacha?.isAccessible}
            isSoldOut={!!currentGacha?.isSoldOut}
            isLimited={!!currentGacha?.isLimited}
            price={currentGacha?.price ?? 0}
            remainingLimitedItemCount={
              currentGacha?.remainingLimitedItemCount ?? 0
            }
            gachaTicket={gachaTicket}
            gpsGachaData={currentGacha?.gpsData}
            isPointOnly={
              currentGacha?.gachaRollType === GachaRollType.PrepaidPoint
            }
            isInside={isInside}
            isShowModalPersonalInfoRequire={isShowModalPersonalInfoRequire}
            onClickTrigger={onClickTrigger}
          />
          <RemainingItemCount
            isLimited={!!currentGacha?.isLimited}
            disabled={
              !!currentGacha?.isSoldOut ||
              (!!currentGacha?.gpsData && !isInside)
            }
            remainingLimitedItemCount={
              currentGacha?.remainingLimitedItemCount ?? 0
            }
            totalLimitedItemCount={currentGacha?.totalLimitedItemCount ?? 0}
          />
        </Stack>
        {/**
         * ガチャ説明モーダル
         */}
        <DescriptionDrawer
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          name={currentGacha?.name ?? ''}
          endAt={currentGacha?.formattedEndAt}
          deliveryDateText={currentGacha?.deliveryDateText}
          desc={currentGacha?.description ?? ''}
        />
      </CellFrame>
    </Box>
  )
}

/**
 * ガチャのデータが空の時に表示する
 */
const EmptyCell: Cell = () => {
  const { t } = useTranslation('gachaDetail')
  return (
    <CellFrame sx={{ px: 6, py: 5 }}>
      <Typography variant="body1" color="text.secondary" align="center">
        {t('gachaEmptyMessage')}
      </Typography>
    </CellFrame>
  )
}

/**
 * ローディング中に表示する
 */
const LoadingCell = () => {
  const { t } = useTranslation('gachaDetail')

  return (
    <CellFrame>
      <Box>
        <Skeleton variant="rectangular" height={315} width="100%" />
        <Stack direction="row" sx={{ mt: 2, mb: 5, px: 5, py: 2 }}>
          <Stack flex={1} justifyContent="center">
            <SkeletonText variant="small" width="75%" />
          </Stack>
          <Stack flex={1} alignItems="flex-end" justifyContent="center">
            <Stack direction="row" spacing={3}>
              <Button variant="outlined" size="small">
                {t('detail')}
              </Button>
              <Button variant="outlined" size="small">
                {t('emissionList')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ px: 5 }}>
        <Stack direction="row" spacing={4} sx={{ py: 2 }}>
          {[0, 1].map(key => (
            <Stack
              key={key}
              flex={1}
              alignItems="center"
              sx={{ height: 72, borderRadius: 2, bgcolor: 'grey.50' }}
            >
              <SkeletonText variant="medium" width="50%" sx={{ mt: 3 }} />
            </Stack>
          ))}
        </Stack>
        <Stack sx={{ mt: 4 }} alignItems="flex-end" flex={1}>
          <SkeletonText variant="small" width="30%" />
        </Stack>
      </Box>
    </CellFrame>
  )
}
